import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/nextjs';

import {
  getDiscountCodeByCode,
  getVerifyCode,
  useGetEvent,
  useGetTierFromShareTokenCommand
} from 'legacy/events/hooks';
import useIdentifyLaunchDarkly from 'hooks/useIdentifyLaunchDarkly';
import { CodeType, DiscountCodeDto, EventForUser } from 'legacy/events/types';
import { ErrorResponse } from 'legacy/common/utils/error';
import MetaPixel, { MetaPixelRef } from 'legacy/common/components/MetaPixel';
import {
  incrementPromoterCodeViews,
  incrementEventViews,
  incrementTrackingLinkViews
} from 'legacy/events/service';
import storage from 'legacy/common/utils/storage';

interface ViewEventProviderProps {
  children: ReactNode;
}

interface ViewEventContextI {
  event: EventForUser;
  isEventLoading: boolean;
  unlockedTier: { id: string; shareToken: string } | null;
  unlockedTierError: ErrorResponse | null;
  onUnlockTierErrorReset: () => void;
  discountCodeData: DiscountCodeDto | null;
  isCodeVerified: boolean;
  discountCodeError: ErrorResponse | null;
  refetchEvent: () => void;
  promoterCode: string | undefined;
  eventError: ErrorResponse | null;
  metaPixelRef: React.MutableRefObject<any>;
}

const ViewEventContext = createContext({} as ViewEventContextI);

const trackUniqueVisitor = (
  storageKey: string,
  trackingValue: string,
  trackingCall: () => Promise<void>
) => {
  const trackedItems = storage.getItem(storageKey);
  try {
    if (trackedItems) {
      const parsedTrackedItems = JSON.parse(trackedItems);
      if (parsedTrackedItems.includes(trackingValue)) {
        return;
      }

      storage.setItem(
        storageKey,
        JSON.stringify([...parsedTrackedItems, trackingValue])
      );
    } else {
      storage.setItem(storageKey, JSON.stringify([trackingValue]));
    }

    trackingCall();
  } catch (error) {
    Sentry.captureMessage(
      `Error while attempting to track ${storageKey}: ${error.message}`,
      { level: 'warning' }
    );
  }
};

const ViewEventProvider = ({ children }: ViewEventProviderProps) => {
  const metaPixelRef = useRef<MetaPixelRef>(null);
  const {
    query: {
      slug: eventId,
      invite,
      tierShareToken,
      p,
      discountCode: discountCodeParam,
      t: trackingId
    }
  } = useRouter();
  const getTierFromShareTokenCommand = useGetTierFromShareTokenCommand();
  const [unlockedTier, setUnlockedTier] = useState<{
    id: string;
    shareToken: string;
  } | null>(null);

  const {
    data: event,
    isLoading: isEventLoading,
    error: eventError,
    refetch: refetchEvent
  } = useGetEvent(eventId as string, {
    shareToken: invite as string
  });

  const { data: discountCodeData } = getDiscountCodeByCode(
    eventId,
    (p || discountCodeParam) as string
  );

  const promoterCode =
    discountCodeData?.codeType === CodeType.PromoterCode
      ? discountCodeData.code
      : undefined;

  const discountData =
    discountCodeData?.codeType === CodeType.DiscountCode
      ? discountCodeData
      : undefined;

  // Identify the user in LaunchDarkly.
  // This is used to track any event specific flags states.
  useIdentifyLaunchDarkly({ eventId: eventId as string });

  /**
   * Checks the validity of the provided discount code whenever we have one.
   */
  const { isSuccess: isCodeVerified, error: verifyCodeError } = getVerifyCode(
    eventId,
    discountCodeData?.code,
    1,
    {
      onSuccess: () => {
        if (discountCodeData?.codeType === CodeType.DiscountCode) {
          toast.success(`Discount code applied: ${discountCodeData?.code}`);
        }
      }
    }
  );

  // Unlock tier if share token is present in URL
  useEffect(() => {
    const shareToken = tierShareToken as string;

    if (unlockedTier || !shareToken || !event?._id) {
      return;
    }

    getTierFromShareTokenCommand.mutate(
      {
        eventId: event._id,
        shareToken
      },
      {
        onSuccess: (tier) => {
          setUnlockedTier({
            id: tier.tierId,
            shareToken
          });

          toast.success(`Private ticket unlocked: ${tier.tierName}`);
        }
      }
    );
  }, [tierShareToken, unlockedTier, event?._id]);

  useEffect(() => {
    const promoterCodeToTrack = (p as string) || promoterCode;

    if (promoterCodeToTrack && event?._id) {
      trackUniqueVisitor('trackedPromoterCodeViews', promoterCodeToTrack, () =>
        incrementPromoterCodeViews(event._id, promoterCodeToTrack)
      );
    }
  }, [promoterCode, p, event?._id]);

  useEffect(() => {
    if (event?._id) {
      trackUniqueVisitor('trackedViews', event._id, () =>
        incrementEventViews(event._id)
      );

      if (trackingId) {
        trackUniqueVisitor('trackedLinkViews', trackingId as string, () =>
          incrementTrackingLinkViews(event._id, trackingId as string)
        );
      }
    }
  }, [event?._id, trackingId]);

  return (
    <ViewEventContext.Provider
      value={{
        event,
        isEventLoading,
        eventError,
        refetchEvent,
        unlockedTier,
        unlockedTierError: getTierFromShareTokenCommand.error,
        onUnlockTierErrorReset: getTierFromShareTokenCommand.reset,
        discountCodeData: discountData,
        isCodeVerified,
        discountCodeError: verifyCodeError,
        metaPixelRef,
        promoterCode
      }}
    >
      {event?.metaPixelId && (
        <MetaPixel ref={metaPixelRef} pixelId={event.metaPixelId} />
      )}
      {children}
    </ViewEventContext.Provider>
  );
};

export const useViewEvent = () => useContext(ViewEventContext);

export default ViewEventProvider;
